import { Link } from "@tanstack/react-router";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Button } from "@project/components/ui/button";
import { Sprout, Syringe, Droplet, HeartPulse, ArrowRight } from "lucide-react";
import { mockAnimals, mockMilkTrend } from "@project/lib/mock-modules";
import {
  AreaChart,
  Area,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export default function GaushalaDashboard() {
  const total = mockAnimals.length;
  const healthy = mockAnimals.filter(a => a.health === "Healthy").length;
  const underCare = mockAnimals.filter(a => a.health === "Under Care").length;
  const vaccinated = mockAnimals.filter(a => a.vaccinated).length;
  const milkToday = mockAnimals.reduce((s, a) => s + a.dailyMilkLitres, 0);

  return (
    <AdminShell
      title="Gaushala"
      subtitle="Animal welfare, health & dairy snapshot"
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/admin/gaushala/animals">
            Animal Directory <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total Animals"
          value={total}
          icon={<Sprout className="h-5 w-5" />}
          accent="from-primary/90 to-primary"
          sub={`${mockAnimals.filter(a => a.species === "Cow").length} cows · ${mockAnimals.filter(a => a.species === "Calf").length} calves`}
        />
        <StatCard
          label="Healthy"
          value={`${healthy} / ${total}`}
          icon={<HeartPulse className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub={`${underCare} under care`}
        />
        <StatCard
          label="Vaccinated"
          value={`${vaccinated} / ${total}`}
          icon={<Syringe className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub={`${total - vaccinated} pending`}
        />
        <StatCard
          label="Milk Today"
          value={`${milkToday} L`}
          icon={<Droplet className="h-5 w-5" />}
          accent="from-sky-600 to-sky-500"
          sub="Across all milch cows"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <SectionCard
          title="Milk Production"
          subtitle="Last 7 days · litres"
          className="lg:col-span-2"
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={mockMilkTrend}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gMilk" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--primary)"
                    stopOpacity={0.45}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--primary)"
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  fontSize: 12,
                }}
                formatter={(v: any) => [`${v} L`, "Milk"]}
              />
              <Area
                type="monotone"
                dataKey="litres"
                stroke="var(--primary)"
                strokeWidth={2.5}
                fill="url(#gMilk)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Vaccination Summary" subtitle="Coverage breakdown">
          <div className="space-y-3">
            {[
              {
                label: "Vaccinated",
                value: vaccinated,
                color: "bg-emerald-500",
              },
              {
                label: "Pending / Calves",
                value: total - vaccinated,
                color: "bg-amber-500",
              },
            ].map(row => {
              const pct = Math.round((row.value / total) * 100);
              return (
                <div key={row.label}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground">
                      {row.label}
                    </span>
                    <span className="text-muted-foreground">
                      {row.value} ({pct}%)
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full ${row.color}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="bg-muted/30 mt-4 rounded-lg border border-border p-3 text-xs text-muted-foreground">
              Next scheduled drive:{" "}
              <span className="font-medium text-foreground">
                FMD booster · 02 Jul 2025
              </span>
            </div>
          </div>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
